import { ChangeEvent, FunctionComponent, useRef, useState } from 'react';
import {
    Card,
    CardContent,
    Drawer,
    Stack,
    SvgIcon,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { StarjackTable } from '@/components/Table/v2/Table';
import { usePagination } from '@starjack/shared-components';
import { GetMailSearchParams, useLazyGetMailsQuery } from '@/api/v2/mails';
import { Mail } from '@/models/mail';
import { LoadingButton } from '@mui/lab';
import { formatStarjackDate } from '@/utils/time';
import { MailDrawerContent } from '@/pageContents/mails/MailDrawerContent';
import IconButton from '@mui/material/IconButton';
import MagnifyingGlassIcon from '@heroicons/react/24/solid/MagnifyingGlassIcon';

export const MailsContent: FunctionComponent = () => {
    const [getMails, { isFetching, data }] = useLazyGetMailsQuery();
    const { currentItems, page, setNextPage, setItemsPerPage, itemsPerPage } =
        usePagination({
            itemsPerPage: 10,
            items: data,
        });
    const [searchParams, setSearchParams] = useState<GetMailSearchParams>({
        userId: null,
        orderId: null,
        to: null,
    });
    const theme = useTheme();
    const [mailDetailConfig, setMailDetailConfig] = useState<{
        isOpen: boolean;
        mail: Mail;
    }>({
        isOpen: false,
        mail: null,
    });
    const getMailsRequested = useRef(false);

    const onFilterTextFieldChange =
        (key: keyof GetMailSearchParams) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            setSearchParams((prevState) => ({
                ...prevState,
                [key]: value || null,
            }));
        };

    const onSearchClick = () => {
        getMailsRequested.current = true;
        getMails(searchParams);
    };

    const onCloseDrawer = () => {
        setMailDetailConfig((prevState) => ({
            ...prevState,
            isOpen: false,
        }));
    };

    return (
        <>
            <Typography sx={{ mb: 8 }} variant='h4'>
                Mails
            </Typography>
            <Stack alignItems='flex-start' spacing={theme.page.verticalSpacing}>
                <Card
                    sx={{
                        width: '100%',
                    }}
                >
                    <CardContent>
                        <Stack
                            sx={{
                                width: '100%',
                                alignItems: 'center',
                            }}
                            spacing={3}
                            direction='row'
                        >
                            <TextField
                                onChange={onFilterTextFieldChange('userId')}
                                value={searchParams.userId}
                                sx={{
                                    display: 'flex',
                                    flexGrow: 1,
                                }}
                                label='User-ID'
                            />
                            <TextField
                                onChange={onFilterTextFieldChange('orderId')}
                                value={searchParams.orderId}
                                sx={{
                                    display: 'flex',
                                    flexGrow: 1,
                                }}
                                label='Order-ID'
                            />
                            <TextField
                                onChange={onFilterTextFieldChange('to')}
                                value={searchParams.to}
                                sx={{
                                    display: 'flex',
                                    flexGrow: 1,
                                }}
                                label='An:'
                            />

                            <LoadingButton
                                onClick={onSearchClick}
                                sx={{
                                    width: 160,
                                }}
                                variant='contained'
                                loading={isFetching}
                            >
                                Suchen
                            </LoadingButton>
                        </Stack>
                    </CardContent>
                </Card>

                <StarjackTable<Mail>
                    count={data?.length}
                    items={currentItems}
                    noDataLabel={
                        !getMailsRequested.current
                            ? 'Mit dem Filter oben nach Mails suchen'
                            : 'Keine Mails gefunden'
                    }
                    columns={[
                        {
                            label: 'Gesendet am',
                            itemTransform: (item) =>
                                formatStarjackDate(item.sentAt),
                        },
                        {
                            label: 'Benutzer',
                            itemKey: 'userId',
                        },
                        {
                            label: 'Bestellung',
                            itemKey: 'orderId',
                        },
                        {
                            label: 'Betreff',
                            itemKey: 'subject',
                        },

                        {
                            label: 'Aktionen',
                            itemTransform: (mail) => (
                                <IconButton
                                    onClick={() => {
                                        setMailDetailConfig({
                                            isOpen: true,
                                            mail,
                                        });
                                    }}
                                >
                                    <SvgIcon>
                                        <MagnifyingGlassIcon />
                                    </SvgIcon>
                                </IconButton>
                            ),
                        },
                    ]}
                    currentPage={page}
                    onPageChange={(nextPage) => setNextPage(nextPage)}
                    itemId='id'
                    onRowsPerPageChange={setItemsPerPage}
                    rowsPerPage={itemsPerPage}
                    loading={isFetching}
                />
            </Stack>

            <Drawer
                anchor='right'
                open={!!mailDetailConfig.isOpen}
                onClose={onCloseDrawer}
            >
                <MailDrawerContent
                    mail={mailDetailConfig.mail}
                    onClose={onCloseDrawer}
                />
            </Drawer>
        </>
    );
};
